import { PublicKey } from "@solana/web3.js";

export const MAX_CLIFF_PERIODS = 8;
export const CLIFF_BPS_DENOM = 10_000;
export const LOCK_TYPE_TIME = 0;
export const LOCK_TYPE_PRICE = 1;
export const LOCK_TYPE_CLIFFS = 2;

/** Anchor account discriminator: sha256("account:CliffSchedule").slice(0, 8) */
export const CLIFF_SCHEDULE_DISC = Buffer.from([
  204, 193, 35, 7, 246, 187, 211, 23,
]);

export function deriveCliffSchedulePda(programId, lockRecordPda) {
  const program =
    programId instanceof PublicKey ? programId : new PublicKey(programId);
  const lockRecord =
    lockRecordPda instanceof PublicKey
      ? lockRecordPda
      : new PublicKey(lockRecordPda);
  const [pda, bump] = PublicKey.findProgramAddressSync(
    [Buffer.from("cliff_schedule"), lockRecord.toBuffer()],
    program,
  );
  return { pda, bump };
}

function readU64LE(buf, offset) {
  const lo = buf.readUInt32LE(offset);
  const hi = buf.readUInt32LE(offset + 4);
  return BigInt(lo) + (BigInt(hi) << 32n);
}

function readI64LE(buf, offset) {
  const u = readU64LE(buf, offset);
  if (u >= 0x8000000000000000n) {
    return Number(u - 0x10000000000000000n);
  }
  return Number(u);
}

/**
 * Decode CliffSchedule account data (Borsh, no padding).
 * Layout after 8-byte disc: pubkey(32) + u64 + u64 + u8 + [i64+u16; 8]
 */
export function decodeCliffSchedule(data) {
  const buf = Buffer.from(data);
  if (buf.length < 57) return null;
  if (!buf.subarray(0, 8).equals(CLIFF_SCHEDULE_DISC)) return null;

  const lockRecord = new PublicKey(buf.subarray(8, 40)).toBase58();
  const originalAmount = readU64LE(buf, 40).toString();
  const withdrawn = readU64LE(buf, 48).toString();
  const count = buf[56];
  const cliffs = [];
  let offset = 57;
  for (let i = 0; i < Math.min(count, MAX_CLIFF_PERIODS); i += 1) {
    if (offset + 10 > buf.length) break;
    cliffs.push({
      unlockTime: readI64LE(buf, offset),
      cumulativeBps: buf.readUInt16LE(offset + 8),
    });
    offset += 10;
  }

  return {
    lockRecord,
    originalAmount,
    withdrawn,
    count,
    cliffs,
  };
}

export function vestedBps(cliffs, nowSec = Math.floor(Date.now() / 1000)) {
  let bps = 0;
  for (const c of cliffs || []) {
    if (nowSec >= Number(c.unlockTime)) bps = Number(c.cumulativeBps) || 0;
  }
  return bps;
}

export function claimableRaw(
  originalAmount,
  withdrawn,
  cliffs,
  nowSec = Math.floor(Date.now() / 1000),
) {
  const bps = vestedBps(cliffs, nowSec);
  const original = BigInt(originalAmount || 0);
  const already = BigInt(withdrawn || 0);
  const vested = (original * BigInt(bps)) / BigInt(CLIFF_BPS_DENOM);
  return vested > already ? vested - already : 0n;
}

export function nextCliffTime(cliffs, nowSec = Math.floor(Date.now() / 1000)) {
  for (const c of cliffs || []) {
    const t = Number(c.unlockTime);
    if (t > nowSec) return t;
  }
  return null;
}

export function cliffProgressLabel(cliffs, nowSec = Math.floor(Date.now() / 1000)) {
  const bps = vestedBps(cliffs, nowSec);
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}% vested`;
}

function vestedRaw(originalAmount, bps) {
  return (BigInt(originalAmount || 0) * BigInt(bps || 0)) / BigInt(CLIFF_BPS_DENOM);
}

/**
 * UI snapshot after partial claims: remaining vs original, next period, period statuses.
 */
export function describeCliffProgress({
  cliffs,
  originalAmount,
  withdrawn,
  remainingAmount,
  nowSec = Math.floor(Date.now() / 1000),
}) {
  const list = Array.isArray(cliffs) ? cliffs : [];
  const original = originalAmount || remainingAmount || "0";
  const already = withdrawn || "0";
  const remaining = remainingAmount || "0";
  const claimable = claimableRaw(original, already, list, nowSec);
  const next = nextCliffTime(list, nowSec);
  const withdrawnBig = BigInt(already);
  const periods = list.map((c, i) => {
    const t = Number(c.unlockTime);
    const cumulativeBps = Number(c.cumulativeBps) || 0;
    const prevBps = i === 0 ? 0 : Number(list[i - 1].cumulativeBps) || 0;
    let status = "locked";
    if (nowSec >= t) {
      status =
        withdrawnBig >= vestedRaw(original, cumulativeBps) ? "claimed" : "ready";
    }
    return {
      index: i + 1,
      unlockTime: t,
      cumulativeBps,
      stepBps: cumulativeBps - prevBps,
      status,
    };
  });
  const claimedCount = periods.filter((p) => p.status === "claimed").length;
  return {
    original,
    withdrawn: already,
    remaining,
    claimable: claimable.toString(),
    nextUnlockTime: next,
    vestedBps: vestedBps(list, nowSec),
    claimedCount,
    totalPeriods: periods.length,
    periods,
    fullyVested: Boolean(list.length) && !next,
  };
}
